// src/lib/attendance.js — pure helpers for attendance calculations

import { timeToMinutes, daySlots, dayScheduledMinutes, scheduledSlotsOnDate } from './schedule.js'

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function toDateStr(date) {
  // Use LOCAL date components — toISOString() is UTC and shifts the date
  // for timezones ahead of UTC (e.g. Georgia UTC+4 between 00:00-04:00 local)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function dayKey(dateStr) {
  return DAY_NAMES[new Date(dateStr + 'T12:00:00').getDay()]
}

export function diffMinutes(start, end) {
  if (!start || !end) return null
  const s = timeToMinutes(start)
  const e = timeToMinutes(end)
  return e > s ? e - s : null
}

export function fmtDuration(minutes) {
  if (minutes == null || minutes < 0) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

export function fmtDelta(minutes) {
  if (minutes == null) return '—'
  if (minutes === 0) return '0'
  const abs = Math.abs(minutes)
  const sign = minutes < 0 ? '-' : '+'
  return sign + fmtDuration(abs)
}

/** Total scheduled minutes for an agent on a given date (multi-slot aware) */
export function scheduledMinutes(agent, dateStr, scheduleHistory = [], overrideMap = {}) {
  const slots = scheduledSlotsOnDate(agent, dateStr, scheduleHistory, overrideMap)
  return slots.reduce((sum, s) => {
    const mins = timeToMinutes(s.end) - timeToMinutes(s.start)
    return sum + (mins > 0 ? mins : 0)
  }, 0)
}

/**
 * Primary location for a day — 'office' if any slot is office, 'home' if only home, 'off' otherwise.
 * For split days returns 'split'.
 */
export function scheduledLocation(agent, dateStr, scheduleHistory = [], overrideMap = {}) {
  const slots = scheduledSlotsOnDate(agent, dateStr, scheduleHistory, overrideMap)
  if (slots.length === 0) return 'off'
  if (slots.length > 1) return 'split'
  return slots[0].location
}

// The attendance status a CHECK-IN should write, from where the roster says this agent is
// working that day (owner-reported bug 2026-08-03: agents rostered at home were checked in
// by a manager and recorded as being in the office — the Listings rail then filed them under
// "ოფისში", and the office-occupancy chart counted them).
//
// The cause is that ONE field answers TWO questions — "did they work" and "from where" —
// so 'present' silently means IN THE OFFICE, and every writer has to remember to say
// otherwise. Five call sites all wrote 'present'. This makes the roster the default answer
// instead of the office, at every one of them.
//
// ⚠️ Still a workaround, not the fix. The real repair is `location` as its own column,
// defaulted from the schedule and overridable, so that pressing "start" cannot relocate
// anybody. Recorded for the owner; not done here because it is a scheduler-DB schema change.
//
// No slots (working an unscheduled day) → 'present': we genuinely do not know where they
// are, and this keeps the previous behaviour rather than inventing a location.
// Mixed slots (a split day, part office part home) → 'present': a single top-level status
// cannot say "both", and claiming remote for a day with an office slot would be the same
// class of lie in the other direction.
export function attendanceStatusFor(agent, dateStr, scheduleHistory = [], overrideMap = {}) {
  const slots = scheduledSlotsOnDate(agent, dateStr, scheduleHistory, overrideMap)
  if (!slots.length) return 'present'
  return slots.every(s => s.location === 'home') ? 'remote' : 'present'
}

// ⚠️ An OPEN legacy record (a check-in, no check-out) returns NULL - "not known yet" (attendance audit 2026-10-21, A02).
// It used to return the agent's CURRENT weekly template, which on a 4-hour override day reported 8 hours worked and
// 4 hours of overtime that no check-out ever established. A record with no times at all is still credited at its
// EFFECTIVE schedule for that date (history + override), never today's template.
export function actualMinutes(record, agent, scheduleHistory = [], overrideMap = {}) {
  if (!record) return null
  // Per-slot model: sum worked minutes across slots, skipping absent/holiday
  if (Array.isArray(record.slots) && record.slots.length > 0) {
    let total = 0
    for (const slot of record.slots) {
      if (slot.status === 'absent' || slot.status === 'holiday') continue
      const mins = diffMinutes(slot.check_in, slot.check_out)
      if (mins != null) total += mins
    }
    return total
  }
  // Legacy single check_in/check_out
  if (record.status === 'absent' || record.status === 'holiday') return 0
  const entered = diffMinutes(record.check_in, record.check_out)
  if (entered != null) return entered
  if (record.check_in && !record.check_out) return null
  return scheduledMinutes(agent, record.date, scheduleHistory, overrideMap)
}

export function dateRange(dateFrom, dateTo) {
  const dates = []
  const cur = new Date(dateFrom + 'T12:00:00')
  const end = new Date(dateTo  + 'T12:00:00')
  while (cur <= end) {
    dates.push(toDateStr(cur))
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

export function weekStart(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return toDateStr(d)
}

export function monthBounds(dateStr) {
  const [y, m] = dateStr.split('-').map(Number)
  const from = `${y}-${String(m).padStart(2,'0')}-01`
  const lastDay = new Date(y, m, 0).getDate()
  const to = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`
  return { from, to }
}

/**
 * Derives the effective top-level status from a record.
 * If slots array is present, uses slot statuses; otherwise falls back to record.status.
 */
export function deriveStatus(record) {
  if (!record) return null
  if (Array.isArray(record.slots) && record.slots.length > 0) {
    const allAbsent  = record.slots.every(s => s.status === 'absent')
    const allHoliday = record.slots.every(s => s.status === 'holiday')
    if (allAbsent)  return 'absent'
    if (allHoliday) return 'holiday'
    // If any slot has home location and none are office-worked, consider remote
    return record.status || 'present'
  }
  return record.status
}

// ⚠️ `lateThresholdMins` is the SAME `config.late_threshold_minutes` the Stats tab passes to
// computeAgentStats, and it must stay that way. Owner 2026-09-10 stated the rule as "late is
// considered more than 5 minutes"; that number lives in Admin, not here, because two hard-coded
// thresholds would make one screen call an arrival late and the other call it on time. The
// default below is only what a caller gets for not passing one.
//
// ⚠️ WHAT "WORKED" MAY CONTAIN (attendance audit 2026-10-21, A01/A02), with `today` = the Tbilisi date:
//   * a FUTURE scheduled day is plannedFutureMins only - never worked, scheduled, present or assumed (it added a full
//     8 h to Worked for days that had not happened, while its own cell rightly showed a dash);
//   * a past scheduled day with no record keeps the long-standing rule - credited to schedule as ASSUMED - but the
//     credited part is also reported as assumedMins, so the page can say how much of Worked is assumed;
//   * an open check-in (no check-out) is credited the same assumed way and never earns overtime;
//   * recorded work on an UNSCHEDULED day counts (worked + overtime + unscheduledMins) - it used to vanish.
export function summarise(agent, dates, attendanceByDate, scheduleHistory = [], overrideMap = {}, lateThresholdMins = 5, today = null) {
  let scheduledMins = 0, workedMins = 0, assumedMins = 0, plannedFutureMins = 0, unscheduledMins = 0
  let confirmedDays = 0, assumedDays = 0, absentDays = 0, presentDays = 0, remoteDays = 0
  // Owner 2026-09-10, for the week and month views: "we need to see how many days have been
  // late… and how many minutes have been late. And how many minutes overtime. How many days
  // გაცდენა." absentDays already existed; these three did not.
  let lateDays = 0, lateMins = 0, overtimeMins = 0

  for (const date of dates) {
    const sched = scheduledMinutes(agent, date, scheduleHistory, overrideMap)
    const rec = attendanceByDate[date]
    if (sched === 0) {
      const ds = rec ? deriveStatus(rec) : null
      if (rec && ds !== 'absent' && ds !== 'holiday') {
        const a = actualMinutes(rec, agent, scheduleHistory, overrideMap)
        if (a) { workedMins += a; overtimeMins += a; unscheduledMins += a; if (rec.confirmed) confirmedDays++ }
      }
      continue
    }
    if (today && date > today) { plannedFutureMins += sched; continue }

    scheduledMins += sched

    if (!rec) {
      workedMins += sched
      assumedMins += sched
      assumedDays++
      presentDays++
    } else {
      // Derive effective status: if all slots are absent → absent, all holiday → holiday
      const derivedStatus = deriveStatus(rec)
      if (derivedStatus === 'absent') {
        absentDays++
      } else if (derivedStatus === 'holiday') {
        scheduledMins -= sched
      } else {
        const actual = actualMinutes(rec, agent, scheduleHistory, overrideMap)
        workedMins += actual ?? sched
        if (actual == null) assumedMins += sched
        // OVERTIME: the positive days only, never the net — the same rule statsCompute follows.
        // +2h on Monday and −2h on Friday is two hours of overtime and a net of zero, and
        // reporting "none" to someone who stayed late is the number they would query first.
        // `deltaMins` below still carries the net; the two are not substitutes.
        if (actual != null && actual > sched) overtimeMins += actual - sched
        // LATENESS against the FIRST scheduled slot's start. ⚠️ Minutes are counted from that
        // start, not from the threshold: the threshold decides WHETHER the day counts as late,
        // it is not a discount on the minutes once it does.
        const firstSlot = scheduledSlotsOnDate(agent, date, scheduleHistory, overrideMap)[0]
        const checkIn = rec.check_in || (Array.isArray(rec.slots) ? rec.slots.find(x => x.check_in)?.check_in : null)
        if (firstSlot?.start && checkIn) {
          const behind = timeToMinutes(checkIn) - timeToMinutes(firstSlot.start)
          if (behind > lateThresholdMins) { lateDays++; lateMins += behind }
        }
        if (rec.confirmed) confirmedDays++
        else assumedDays++
        if (derivedStatus === 'remote') remoteDays++
        else presentDays++
      }
    }
  }

  return {
    scheduledMins, workedMins, assumedMins, plannedFutureMins, unscheduledMins,
    deltaMins: workedMins - scheduledMins,
    confirmedDays, assumedDays, absentDays, presentDays, remoteDays,
    lateDays, lateMins, overtimeMins,
  }
}
